import { Box } from '@chakra-ui/react';
import { useEditor, EditorContent } from '@tiptap/react';
import { useEffect, useState } from 'react';
import { useField } from 'formik';
import { sanitizeDom } from '@/lib/purify';
import debounce from 'lodash/debounce';
import Placeholder from '@tiptap/extension-placeholder';
import VariableHighlightDecoration from '@/ui/editor/extensions/variable-highlight-decoration';
import Document from '@tiptap/extension-document';
import Paragraph from '@tiptap/extension-paragraph';
import TiptapText from '@tiptap/extension-text';
import History from '@tiptap/extension-history';
import CharacterCount from '@tiptap/extension-character-count';
import EditorCustomVariables from './EditorCustomVariables';
import { useMutation } from '@tanstack/react-query';
import { getPlaceholderPreview } from './api';
import { SanitizedBox } from '@/ui/SanitizedDom';
import {
  addTooltipToUnsupportedVariables,
  addVariableHoverListeners,
} from './helper';

export const VariableInlineEditor = ({ item = {}, field, formik }) => {
  const {
    name,
    editorContainerStyles = {},
    content = '',
    editable = true,
    replaceContent = true,
    editorStyles = {},
    enableCustomVariables = false,
    customVariableList,
    placeholder = '',
    getEditorInstance = false,
    customEditorClassName = '',
    maxLength = 150,
    variableWrapperProps = {},
    showErrorStyles = false,
    isRequired,
    templateIsPreview = false,
    previewProps = {
      enable: false,
    },
  } = item;
  const [_field, meta, helpers] = useField(name); // eslint-disable-line
  const { setValue, setTouched, setError } = helpers;
  const { setValues, values } = formik || {};
  const variablesListArr = Object.entries(customVariableList || {}).flatMap(
    ([, values = []]) => values
  );
  const { submitCount } = formik || {};
  const { value, touched } = meta || {};

  const setEditorValue = (editor) => {
    setValue(editor.getText());
    setError(null);
  };

  const [previewData, setPreviewData] = useState(null);

  const previewMutation = useMutation({
    mutationFn: getPlaceholderPreview,
    onSuccess: (data) => {
      setPreviewData(data);
      setTimeout(() => {
        addVariableHoverListeners();
      }, 0);
    },
  });

  useEffect(() => {
    if (templateIsPreview) {
      let previewPayload = {
        ...{
          body: meta?.value || content,
          retain_variables: true,
        },
        ...previewProps.payload,
      };
      previewMutation.mutate(previewPayload);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateIsPreview]);

  const editor = useEditor({
    extensions: [
      Document.extend({
        content: 'block',
      }),
      History,
      Paragraph.extend({
        addAttributes() {
          return { class: { default: 'single-line' } };
        },
      }),
      TiptapText,
      Placeholder.configure({ placeholder }),
      CharacterCount.configure({
        limit: maxLength,
      }),
      enableCustomVariables &&
        customVariableList &&
        VariableHighlightDecoration.configure({
          enabled: enableCustomVariables,
          supportedVariables: variablesListArr,
        }),
    ],
    editable,
    content: sanitizeDom(content),
    onUpdate: debounce(({ editor }) => {
      setEditorValue(editor);
      setTouched(true); // Mark as touched when user makes changes
    }, 300),
    onCreate({ editor }) {
      editor?.commands?.setContent(meta?.value || content);
      setEditorValue(editor);
    },
  });

  const showError =
    (submitCount > 0 || touched) &&
    (!value?.toString()?.length > 0 || meta?.error) &&
    !showErrorStyles &&
    isRequired;

  useEffect(() => {
    if (!editor) {
      return undefined;
    }
    editor.setEditable(editable);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editable]);

  useEffect(() => {
    const { editorInstance } = values || {};
    if (!editorInstance && getEditorInstance && editor) {
      setValues({ ...values, editorInstance: editor });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, getEditorInstance]);

  useEffect(() => {
    if (replaceContent) {
      setValue(content);
      editor?.commands?.setContent(content || meta?.value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content, replaceContent]);

  useEffect(() => {
    if (!editor || !customVariableList || !enableCustomVariables) return;
    editor?.commands?.updateVariablesList?.(variablesListArr);
    editor.setOptions({
      editorProps: { supportedVariables: variablesListArr },
    });

    // Add focus restoration after variable insertion
    const handleVariableInsertion = () => {
      editor.commands.focus();
    };

    // Listen for custom variable insertion events
    const handleCustomEvent = (event) => {
      if (event.detail?.type === 'kula_editor_variable_inserted') {
        const eventEditorName = event.detail?.editorName;
        const eventEditorType = event.detail?.editorType;

        if (eventEditorType === 'input' || eventEditorName === name) {
          handleVariableInsertion();
        }
      }
    };
    document.addEventListener(
      'kula_editor_variable_inserted',
      handleCustomEvent
    );

    return () => {
      document.removeEventListener(
        'kula_editor_variable_inserted',
        handleCustomEvent
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customVariableList, editor]);

  return (
    <>
      <Box
        className={`editor-style variable-inline-editor ${customEditorClassName} `}
        borderRadius="8px"
        overflow="hidden"
        py="12px"
        display="flex"
        gap="5px"
        _hover={{
          outline: '4px solid #6220A30F !important',
          border: '2px solid #605AFB !important',
          boxShadow: 'none !important',
        }}
        _focus={{
          outline: '4px solid #6220A30F !important',
          border: '2px solid #605AFB !important',
          boxShadow: 'none !important',
        }}
        border={'2px solid #E5E5E5 !important'}
        boxShadow={showError ? '0 0 0 2px #A32020 !important' : 'none'}
        {...editorContainerStyles}
      >
        <Box
          style={{
            height: '21px',
            overflow: 'hidden',
            flex: '1',
            ...editorStyles,
          }}
        >
          {!templateIsPreview ? (
            <EditorContent
              editor={editor}
              id={name}
              onBlur={() => setTouched(true)}
            />
          ) : (
            <Box>
              <SanitizedBox
                fontSize="14px"
                html={addTooltipToUnsupportedVariables(previewData?.body)}
                color="secondary-700"
              />
            </Box>
          )}
        </Box>
        {/* here we removing the character count as a part of UX audit */}
        {/* {maxLength && (
          <KText
            size="sm-medium"
            fontSize="12px !important"
            color="secondary-500"
          >
            {field?.value?.length || 0}/{maxLength}
          </KText>
        )} */}
        {enableCustomVariables && (
          <Box
            pointerEvents={templateIsPreview ? 'none' : 'all'}
            opacity={templateIsPreview ? 0.5 : 1}
          >
            <EditorCustomVariables
              type="input"
              config={customVariableList}
              editor={editor}
              wrapperProps={{ pt: '0px', ...variableWrapperProps }}
            />
          </Box>
        )}
      </Box>
    </>
  );
};
